import { useContext, useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  Badge,
  Button,
  CodePanel,
  type BadgeVariant,
  type CodeHighlightedLine,
} from '@/components'
import { tokenize } from '../lib/shikiHighlighter'
import { ThemeContext } from '../providers/ThemeProvider'
import { botApi, type BacktestCodeResult, type BacktestListItemByCode } from '../bot/api'

type PageTab = 'code' | 'backtests'

const HEADER_HEIGHT = 56

/** Aligns with server-ai BOT_BACKTEST_STATUS */
const BT_STATUS = {
  PENDING: 0,
  RUNNING: 1,
  COMPLETED: 100,
  ERROR: 200,
  CANCELED: 201
} as const

function formatBacktestStatus(status: number): string {
  switch (status) {
    case BT_STATUS.PENDING:
      return 'Pending'
    case BT_STATUS.RUNNING:
      return 'Running'
    case BT_STATUS.COMPLETED:
      return 'Completed'
    case BT_STATUS.ERROR:
      return 'Error'
    case BT_STATUS.CANCELED:
      return 'Canceled'
    default:
      return String(status)
  }
}

function StatusBadge({ status }: { status: number }) {
  const label = formatBacktestStatus(status)
  let variant: BadgeVariant = 'secondary'
  let extra = ''
  switch (status) {
    case BT_STATUS.COMPLETED:
      variant = 'outline'
      extra = 'border-success/30 bg-success-dim text-success'
      break
    case BT_STATUS.ERROR:
      variant = 'outline'
      extra = 'border-danger/30 bg-danger-dim text-danger'
      break
    case BT_STATUS.CANCELED:
      variant = 'outline'
      extra = 'text-muted-foreground'
      break
    default:
      variant = 'secondary'
  }
  return <Badge variant={variant} className={extra}>{label}</Badge>
}

function VersionBadge({ version, codeVersion }: { version?: number; codeVersion?: number }) {
  const label =
    codeVersion != null && version != null
      ? `v${codeVersion}.${version}`
      : version != null
        ? `v${version}`
        : null

  if (!label) return null

  return (
    <Badge variant="outline" className="font-mono text-muted-foreground">
      {label}
    </Badge>
  )
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  } catch {
    return iso
  }
}

function CodeContent({ code }: { code: string }) {
  const themeCtx = useContext(ThemeContext) as { theme?: string } | undefined
  const isDark = themeCtx?.theme === 'dark'

  const [highlightedLines, setHighlightedLines] = useState<CodeHighlightedLine[] | undefined>(undefined)

  useEffect(() => {
    if (!code) return
    let cancelled = false
    tokenize(code, isDark ? 'github-dark' : 'github-light')
      .then((tokens) => {
        if (!cancelled) setHighlightedLines(tokens)
      })
      .catch((err) => {
        console.error('Failed to tokenize code:', err)
      })
    return () => {
      cancelled = true
    }
  }, [code, isDark])

  const isEmpty = !code
  return (
    <CodePanel
      code={code}
      highlightedLines={highlightedLines}
      isEmpty={isEmpty}
      emptyLabel="No code available for this version."
    />
  )
}

export default function BotCodePage() {
  const { cid: rawCid } = useParams<{ cid: string }>()
  const cid = rawCid?.trim() ?? ''
  const location = useLocation()
  const activeTab: PageTab = location.pathname.endsWith('/backtests') ? 'backtests' : 'code'

  const [codeData, setCodeData] = useState<BacktestCodeResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [backtests, setBacktests] = useState<BacktestListItemByCode[] | null>(null)
  const [backtestsLoading, setBacktestsLoading] = useState(false)
  const [backtestsError, setBacktestsError] = useState<string | null>(null)

  useEffect(() => {
    if (!cid) {
      setCodeData(null)
      setIsLoading(false)
      setErrorMessage(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setCodeData(null)
    setErrorMessage(null)

    botApi
      .getCodeByCid(cid)
      .then(data => {
        if (cancelled) return
        if (!data) {
          setErrorMessage('Code not found.')
          return
        }
        setCodeData(data)
      })
      .catch(err => {
        if (cancelled) return
        const isNotFound = err?.errorCode === 'CODE_NOT_FOUND'
        if (isNotFound) {
          setErrorMessage('Code not found.')
          return
        }
        setErrorMessage('Failed to load code. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [cid])

  useEffect(() => {
    if (!cid || activeTab !== 'backtests') {
      return
    }

    let cancelled = false
    setBacktestsLoading(true)
    setBacktestsError(null)
    setBacktests(null)

    botApi
      .getBacktestsByCid(cid)
      .then(rows => {
        if (cancelled) return
        setBacktests(rows)
      })
      .catch(err => {
        if (cancelled) return
        const isNotFound = err?.errorCode === 'CODE_NOT_FOUND'
        if (isNotFound) {
          setBacktestsError('Code not found.')
          return
        }
        setBacktestsError('Failed to load backtests.')
      })
      .finally(() => {
        if (!cancelled) setBacktestsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [cid, activeTab])

  if (!cid) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-error)' }}
      >
        Invalid code ID. Use /code/&lt;cid&gt; in the URL.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-text-grey)' }}
      >
        Loading code...
      </div>
    )
  }

  if (errorMessage || !codeData) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-error)' }}
      >
        {errorMessage || 'Code not found.'}
      </div>
    )
  }

  const backToBotHref = codeData.bid ? `/strategy/${codeData.bid}` : ''

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        backgroundColor: 'var(--base-100)',
        overflow: 'hidden'
      }}
    >
      <div
        className="px-4"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: HEADER_HEIGHT,
          borderBottom: '1px solid var(--color-line)',
          backgroundColor: 'var(--base-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}
      >
        <div className="inline-flex items-center gap-2">
          {backToBotHref ? (
            <Link
              to={backToBotHref}
              className="inline-flex items-center gap-2 text-sm"
              style={{
                color: 'var(--base-content)',
                textDecoration: 'none'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
              <span>Back to builder</span>
            </Link>
          ) : (
            <div className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-grey)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
              <span>Back to builder</span>
            </div>
          )}
          {codeData.version != null && <VersionTag version={codeData.version} />}
        </div>

        <div className="flex items-center gap-2">
          <HeaderTabLink label="Code" to={`/code/${cid}`} isActive={activeTab === 'code'} />
          <HeaderTabLink label="Backtests" to={`/code/${cid}/backtests`} isActive={activeTab === 'backtests'} />
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: HEADER_HEIGHT,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden'
        }}
      >
        {activeTab === 'code' && (
          <div className="h-full min-h-0 overflow-hidden">
            <CodeContent code={codeData.code} />
          </div>
        )}

        {activeTab === 'backtests' && (
          <div className="h-full overflow-y-auto px-4 py-6 bg-background">
            <div className="max-w-3xl mx-auto">
              {backtestsLoading && (
                <div className="flex items-center justify-center py-12 text-body text-muted-foreground">
                  Loading backtests...
                </div>
              )}
              {!backtestsLoading && backtestsError && (
                <div className="flex items-center justify-center py-12 text-body text-danger">
                  {backtestsError}
                </div>
              )}
              {!backtestsLoading && !backtestsError && backtests && backtests.length === 0 && (
                <div className="flex items-center justify-center py-12 text-body text-muted-foreground">
                  No backtests for this code version yet.
                </div>
              )}
              {!backtestsLoading && !backtestsError && backtests && backtests.length > 0 && (
                <ul className="flex flex-col gap-3">
                  {backtests.map(row => {
                    const dateRange =
                      row.startDate || row.endDate
                        ? `${row.startDate ?? '—'} → ${row.endDate ?? '—'}`
                        : '—'

                    return (
                      <li
                        key={row.btid}
                        className="rounded-md border border-border bg-card p-4 flex flex-col gap-3 md:flex-row md:items-center md:gap-4"
                      >
                        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-body-strong text-foreground font-mono">
                              {row.symbol ?? '—'} · {row.timeframe ?? '—'}
                            </span>
                            <VersionBadge version={row.version} codeVersion={codeData.version} />
                            <StatusBadge status={row.status} />
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-muted-foreground">
                            <span className="font-mono">{dateRange}</span>
                            <span className="opacity-60">·</span>
                            <span>Created {formatDateTime(row.created)}</span>
                          </div>
                        </div>
                        {row.status === BT_STATUS.COMPLETED && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="self-start md:self-center"
                          >
                            <Link to={`/backtest/${row.btid}`}>View backtest</Link>
                          </Button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function HeaderTabLink({
  label,
  to,
  isActive
}: {
  label: string
  to: string
  isActive: boolean
}) {
  return (
    <Link
      to={to}
      className="px-3 py-1.5 rounded-md text-sm transition-colors"
      style={{
        backgroundColor: isActive ? 'var(--base-300)' : 'transparent',
        color: isActive ? 'var(--base-content)' : 'var(--color-text-grey)',
        border: `1px solid ${isActive ? 'var(--color-line)' : 'transparent'}`,
        textDecoration: 'none'
      }}
    >
      {label}
    </Link>
  )
}

function VersionTag({ version, codeVersion }: { version?: number; codeVersion?: number }) {
  const label =
    codeVersion != null && version != null
      ? `v${codeVersion}.${version}`
      : version != null
        ? `v${version}`
        : null

  if (!label) {
    return null
  }

  return (
    <span
      className="inline-flex shrink-0 items-center px-2 py-0.5 rounded text-xs font-mono"
      style={{
        backgroundColor: 'var(--base-300)',
        color: 'var(--color-text-grey)',
        border: '1px solid var(--color-line)'
      }}
    >
      {label}
    </span>
  )
}
